import { useState } from 'react';
import { cn } from '@/lib/utils';
import { CalendarRangePicker } from './CalendarRangePicker';
import { useCashFlowWorkspace } from './CashFlowWorkspace';
import {
  monthLabel,
  monthRange,
  quarterLabel,
  quarterRange,
  rangeLabel,
  windowMonth,
  yearRange,
} from '@/lib/cashFlow';

const SHORT_MONTHS = ['Jaan', 'Veebr', 'Märts', 'Apr', 'Mai', 'Juuni', 'Juuli', 'Aug', 'Sept', 'Okt', 'Nov', 'Dets'];
const QUARTERS = ['I', 'II', 'III', 'IV'];

const pad2 = (n: number): string => String(n).padStart(2, '0');

/**
 * The ONE period navigator of the cash-flow primary view — rendered IDENTICALLY by
 * every lens (calendar, table, register) so they always scope to the same window
 * (replit.md L14: the lenses share every function and read one workspace state; a
 * period control living in only one lens — or two that drift apart — is a defect).
 * Year ‹ › steppers + a trigger (the active period label, else the bare year) + a
 * popover offering the whole year, a month grid, the four quarters and a visual range
 * picker. It writes the SHARED workspace window/year/label/granularity and reflects the
 * active month back via `windowMonth`; it carries no money or date math itself (L9).
 */
export function PeriodPicker() {
  const { year, setYear, windowRange, setWindowRange, periodText, setPeriodText, setGranularity } =
    useCashFlowWorkspace();
  const [open, setOpen] = useState(false);

  const selectedMonth = windowMonth(windowRange);

  const stepYear = (delta: number) => {
    const next = year + delta;
    setYear(next);
    setWindowRange(yearRange(next));
    setPeriodText(null);
    setGranularity('month');
  };
  const applyYear = () => {
    setWindowRange(yearRange(year));
    setPeriodText(null);
    setGranularity('month');
    setOpen(false);
  };
  const applyMonth = (monthIndex: number) => {
    const month = monthIndex + 1;
    setWindowRange(monthRange(year, month));
    setPeriodText(monthLabel(year, month));
    setGranularity('month');
    setOpen(false);
  };
  const applyQuarter = (quarter: number) => {
    setWindowRange(quarterRange(year, quarter));
    setPeriodText(quarterLabel(year, quarter));
    setGranularity('quarter');
    setOpen(false);
  };
  const applyRange = (from: string, to: string) => {
    setWindowRange({ from, to });
    setPeriodText(rangeLabel(from, to));
    setGranularity('day');
    setOpen(false);
  };

  return (
    <div className="relative flex items-center gap-0.5 text-sm">
      <button
        type="button"
        aria-label="Eelmine aasta"
        onClick={() => stepYear(-1)}
        className="rounded px-1.5 py-0.5 leading-none text-muted-foreground hover:bg-accent hover:text-foreground"
      >
        ‹
      </button>
      <button
        type="button"
        aria-label="Vali periood"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="min-w-24 rounded px-1.5 py-0.5 text-center font-medium text-foreground hover:bg-accent"
      >
        {periodText ?? `${year}`}
      </button>
      <button
        type="button"
        aria-label="Järgmine aasta"
        onClick={() => stepYear(1)}
        className="rounded px-1.5 py-0.5 leading-none text-muted-foreground hover:bg-accent hover:text-foreground"
      >
        ›
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" aria-hidden="true" onClick={() => setOpen(false)} />
          <div
            role="dialog"
            aria-label="Periood"
            className="absolute left-0 top-full z-50 mt-1 w-72 rounded-md border border-border bg-card p-3 shadow-xl"
          >
            <button
              type="button"
              onClick={applyYear}
              aria-pressed={periodText === null}
              className={cn(
                'mb-2 w-full rounded border px-2 py-1 text-xs font-semibold transition-colors',
                periodText === null
                  ? 'border-primary/60 bg-primary/15 text-primary'
                  : 'border-border text-foreground hover:bg-accent',
              )}
            >
              Kogu aasta {year}
            </button>
            <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Kuu</div>
            <div className="grid grid-cols-4 gap-1">
              {SHORT_MONTHS.map((m, i) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => applyMonth(i)}
                  aria-pressed={selectedMonth === pad2(i + 1)}
                  className={cn(
                    'rounded border px-1 py-1 text-[11px] transition-colors',
                    selectedMonth === pad2(i + 1)
                      ? 'border-primary/60 bg-primary/15 text-primary'
                      : 'border-border text-muted-foreground hover:bg-accent hover:text-foreground',
                  )}
                >
                  {m}
                </button>
              ))}
            </div>
            <div className="mb-1 mt-3 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Kvartal</div>
            <div className="grid grid-cols-4 gap-1">
              {QUARTERS.map((q, i) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => applyQuarter(i + 1)}
                  className="rounded border border-border px-1 py-1 text-[11px] text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                >
                  {q}
                </button>
              ))}
            </div>
            <CalendarRangePicker
              year={year}
              month={selectedMonth === 'all' ? 1 : Number(selectedMonth)}
              onApply={applyRange}
            />
          </div>
        </>
      )}
    </div>
  );
}
